import { BlobStore, LocalStore, LocalWriteError } from '@/platform/localStore';
import type { Artwork, ArtworkRevision } from '@/contracts/family';
import type { EngineSnapshot } from '@/features/studio/engine';
import type { ColoringTemplate } from '@/contracts/content';
import { SyncService } from './sync';

/**
 * Artwork persistence (spec §9.3).
 *
 * The save sequence is deliberately staged so the UI can tell the truth:
 *   1. write locally           -> "Saved on this device"
 *   2. (later) upload + commit -> "Saved everywhere"
 * Step 2 is not wired to a backend yet, so the product says "Saved on this
 * device" and nothing more. It never claims a sync that did not happen.
 */

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved-local'; at: number }
  | { kind: 'saved-everywhere'; at: number }
  | { kind: 'failed'; reason: 'quota' | 'unavailable' | 'unknown'; at: number };

/** One id per app launch — used to preserve two-device edits (spec §9.4). */
export const DEVICE_SESSION_ID = `dev_${Math.random().toString(36).slice(2, 10)}`;

function now() {
  return new Date().toISOString();
}

export const ArtworkService = {
  /** Resolves the artwork for a child + template, creating it on first visit. */
  async openForTemplate(
    account: string,
    childId: string,
    storyId: string,
    templateId: string,
    templateRevision: number,
    title: string,
  ): Promise<Artwork> {
    const all = await LocalStore.list<Artwork>('artworks', account);
    // Match on the permanent template ID, never on an array index or an image
    // URL — that is what made old drafts orphan (spec §19.1).
    const existing = all.find(
      (a) =>
        a.childId === childId &&
        a.templateId === templateId &&
        a.lifecycle !== 'withdrawn' &&
        // A finished picture is kept; a new visit resumes the draft or starts
        // a NEW artwork rather than overwriting the finished one (spec §7 C10).
        a.finishedRevisionId === null,
    );
    if (existing) return existing;

    const artwork: Artwork = {
      id: `art_${crypto.randomUUID()}`,
      ownerUserId: account,
      childId,
      storyId,
      templateId,
      templateRevision,
      creativeMode: 'coloring',
      title,
      draftRevisionId: null,
      finishedRevisionId: null,
      lifecycle: 'draft',
      createdAt: now(),
      updatedAt: now(),
    };
    await LocalStore.put('artworks', account, artwork.id, artwork);
    void SyncService.queue('artworks', artwork.id, { childId: artwork.childId });
    return artwork;
  },

  /**
   * Opens a blank page for a drawing of the child's own (spec §0.1 F12).
   *
   * Unlike a story template, a paper is not one-per-child: a child may draw a
   * hundred wide pictures, and each is its own artwork. So this always starts a
   * new one rather than resuming — the shelf is where earlier drawings live.
   */
  async openPaper(
    account: string,
    childId: string,
    paper: { id: string; revision: number; title: string },
    storyId: string | null,
    title: string,
  ): Promise<Artwork> {
    const artwork: Artwork = {
      id: `art_${crypto.randomUUID()}`,
      ownerUserId: account,
      childId,
      // A drawing prompted by a story keeps that link, so it can be found
      // beside the story it came from; a free drawing has no story at all.
      storyId,
      templateId: paper.id,
      templateRevision: paper.revision,
      creativeMode: storyId ? 'extension' : 'free-draw',
      title,
      draftRevisionId: null,
      finishedRevisionId: null,
      lifecycle: 'draft',
      createdAt: now(),
      updatedAt: now(),
    };
    await LocalStore.put('artworks', account, artwork.id, artwork);
    void SyncService.queue('artworks', artwork.id, { childId: artwork.childId });
    return artwork;
  },

  /** Deliberately starts a fresh picture beside an existing finished one. */
  async startAnother(
    account: string,
    from: Artwork,
  ): Promise<Artwork> {
    const artwork: Artwork = {
      ...from,
      id: `art_${crypto.randomUUID()}`,
      draftRevisionId: null,
      finishedRevisionId: null,
      lifecycle: 'draft',
      createdAt: now(),
      updatedAt: now(),
    };
    await LocalStore.put('artworks', account, artwork.id, artwork);
    void SyncService.queue('artworks', artwork.id, { childId: artwork.childId });
    return artwork;
  },

  async get(account: string, artworkId: string) {
    return LocalStore.get<Artwork>('artworks', account, artworkId);
  },

  async listForChild(account: string, childId: string): Promise<Artwork[]> {
    const all = await LocalStore.list<Artwork>('artworks', account);
    return all
      .filter((a) => a.childId === childId)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  },

  async loadDraft(account: string, artworkId: string): Promise<EngineSnapshot | undefined> {
    return LocalStore.get<EngineSnapshot>('drafts', account, artworkId);
  },

  /**
   * Step 1 of the save sequence: a durable local write. Throws
   * `LocalWriteError` so the editor can keep the in-memory canvas and offer a
   * recovery action rather than silently losing the picture (spec §9.3).
   */
  async saveDraft(account: string, artwork: Artwork, snapshot: EngineSnapshot): Promise<void> {
    await LocalStore.put('drafts', account, artwork.id, snapshot);
    // Read-modify-write, never spread the caller's copy.
    //
    // The editor holds an `Artwork` from when the screen mounted. An autosave
    // debounced from the last brush stroke can land AFTER the child pressed
    // Done, and writing that stale object back would erase the
    // `finishedRevisionId` that Done had just set — leaving a finished picture
    // recorded as a draft. Locally that looked harmless; the moment a second
    // device existed it became a picture that was finished on the iPad and a
    // draft everywhere else.
    const stored = (await LocalStore.get<Artwork>('artworks', account, artwork.id)) ?? artwork;
    await LocalStore.put('artworks', account, artwork.id, {
      ...stored,
      draftRevisionId: `draft_${artwork.id}`,
      updatedAt: now(),
    });
    void SyncService.queue('artworks', artwork.id, { childId: artwork.childId });
  },

  /**
   * Step 5: commit an immutable finished revision. Repeating the request with
   * the same idempotency key returns the same revision rather than creating a
   * duplicate (spec §14.4).
   */
  async finish(
    account: string,
    artwork: Artwork,
    snapshot: EngineSnapshot,
    preview: Blob,
    idempotencyKey: string,
  ): Promise<ArtworkRevision> {
    const existingKey = await LocalStore.get<string>('meta', account, `idem_${idempotencyKey}`);
    if (existingKey) {
      const prior = await LocalStore.get<ArtworkRevision>('revisions', account, existingKey);
      if (prior) return prior;
    }

    const revisionId = `rev_${crypto.randomUUID()}`;
    const previewRef = `preview_${revisionId}`;
    await BlobStore.put(account, previewRef, preview);

    let strokeRef: string | null = null;
    if (snapshot.strokeLayer) {
      strokeRef = `stroke_${revisionId}`;
      await LocalStore.put('meta', account, strokeRef, snapshot.strokeLayer);
    }

    const priorRevisions = await LocalStore.list<ArtworkRevision>('revisions', account);
    const mine = priorRevisions.filter((r) => r.artworkId === artwork.id);
    const revNumber = mine.length + 1;

    const revision: ArtworkRevision = {
      id: revisionId,
      artworkId: artwork.id,
      ownerUserId: account,
      templateId: artwork.templateId,
      templateRevision: artwork.templateRevision,
      width: snapshot.width,
      height: snapshot.height,
      regionColors: { ...snapshot.regionColors },
      strokeLayerRef: strokeRef,
      previewRef,
      revision: revNumber,
      baseRevision: mine.length,
      immutable: true,
      createdAt: now(),
      deviceSessionId: DEVICE_SESSION_ID,
    };

    await LocalStore.put('revisions', account, revisionId, revision);
    // Queued only AFTER the durable local write resolved (spec §9.3).
    void SyncService.queue('revisions', revisionId, { childId: artwork.childId });
    await LocalStore.put('meta', account, `idem_${idempotencyKey}`, revisionId);
    const storedArtwork = (await LocalStore.get<Artwork>('artworks', account, artwork.id)) ?? artwork;
    await LocalStore.put('artworks', account, artwork.id, {
      ...storedArtwork,
      // The draft head is kept until the result screen is shown, so a failure
      // between commit and navigation never loses the picture (spec §9.3).
      finishedRevisionId: revisionId,
      lifecycle: 'finished',
      updatedAt: now(),
    });
    void SyncService.queue('artworks', artwork.id, { childId: artwork.childId });
    return revision;
  },

  async getRevision(account: string, revisionId: string) {
    return LocalStore.get<ArtworkRevision>('revisions', account, revisionId);
  },

  async listRevisions(account: string, artworkId: string): Promise<ArtworkRevision[]> {
    const all = await LocalStore.list<ArtworkRevision>('revisions', account);
    return all.filter((r) => r.artworkId === artworkId).sort((a, b) => a.revision - b.revision);
  },

  async previewUrl(account: string, revision: ArtworkRevision): Promise<string | null> {
    if (!revision.previewRef) return null;
    return BlobStore.url(account, revision.previewRef);
  },

  /** Rebuilds an engine snapshot from a stored revision (for re-editing). */
  async snapshotFromRevision(
    account: string,
    revision: ArtworkRevision,
  ): Promise<EngineSnapshot> {
    const strokeLayer = revision.strokeLayerRef
      ? ((await LocalStore.get<string>('meta', account, revision.strokeLayerRef)) ?? null)
      : null;
    return {
      templateId: revision.templateId ?? '',
      templateRevision: revision.templateRevision ?? 1,
      width: revision.width,
      height: revision.height,
      regionColors: revision.regionColors,
      strokeLayer,
      opCount: 0,
    };
  },
};

/**
 * Re-renders a stored revision, optionally omitting regions and the
 * background. Only possible because a revision keeps vector state rather than
 * a flattened image (spec §9.3, §10.2).
 */
export async function renderRevision(
  account: string,
  revision: ArtworkRevision,
  template: ColoringTemplate,
  opts: { background?: string | null; omitRegions?: string[]; scale?: number } = {},
): Promise<string> {
  const { ArtEngine } = await import('@/features/studio/engine');
  const engine = new ArtEngine(template);
  await engine.restore(await ArtworkService.snapshotFromRevision(account, revision));
  return engine.toDataUrl(opts.scale ?? 1, opts.background ?? null, opts.omitRegions);
}

export { LocalWriteError };
